import { forwardRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import visuallyHidden from '@mui/utils/visuallyHidden';
import VideoCallModal from '../../components/VideoCallModal/VideoCallModal';
import type { LearningTeachingRegionProps } from './TextbookTeachingRegion';

export const AvatarTeachingRegion = forwardRef<
  HTMLHeadingElement,
  LearningTeachingRegionProps
>(function AvatarTeachingRegion({
  persona,
  material,
  conversationBoundaryKey,
  conversationSessionId,
  allowNewChat,
  onNewChat,
  onTeachingInteraction,
  onChatSessionIdentified,
  onConversationIdentified,
  onTranscriptVisibilityChange,
}, headingRef) {
  const [callOpen, setCallOpen] = useState(true);

  return (
    <Box
      aria-label="Avatar teaching session"
      className="learning-workspace-page__teaching-region learning-workspace-page__teaching-region--avatar"
      component="section"
      sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <Typography
        component="h2"
        ref={headingRef}
        tabIndex={-1}
        sx={visuallyHidden}
      >
        Avatar teaching session
      </Typography>
      {allowNewChat && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <Button
            onClick={() => {
              onTeachingInteraction?.();
              onNewChat?.();
            }}
            variant="outlined"
          >
            New chat
          </Button>
        </Box>
      )}
      <VideoCallModal
        open={callOpen}
        onClose={() => setCallOpen(false)}
        persona={persona}
        continuationBoundaryKey={conversationBoundaryKey}
        continuationChatId={conversationSessionId}
        // ponytail: LearningWorkspacePage does not currently pass onChatSessionIdentified
        // here (avatar record linking runs through onConversationIdentified instead), so
        // this is inert today. Before wiring it to something that writes the lease (e.g.
        // rememberConversation), see the staleness warning on VideoCallModal's onCallEnded
        // prop — it fires from a latest-ref, so a dying call can otherwise write its id
        // into a lease generation started by a later New chat.
        onCallEnded={onChatSessionIdentified}
        presentation="inline"
        chapterSlug={material.slug}
        openingPreamble={material.preambleMarkdown}
        onTeachingInteraction={onTeachingInteraction}
        onConversationIdentified={onConversationIdentified}
        onTranscriptVisibilityChange={onTranscriptVisibilityChange}
      />
      {!callOpen && (
        <Box sx={{ display: 'grid', flex: '1 1 auto', placeContent: 'center' }}>
          <Button onClick={() => {
            onTeachingInteraction?.();
            setCallOpen(true);
          }} variant="contained">
            Start avatar session
          </Button>
        </Box>
      )}
    </Box>
  );
});
